/**
 * MachineryEditorDrawer — полиморфный редактор модели техники (AK-235).
 *
 * Секции:
 *   1. Идентификация (название RU + годы выпуска)
 *   2. Классификация (группа → тип → производитель). Группа определяет набор
 *      полей секции 3 (полиморф из machinery_groups.fields_schema).
 *   3. Характеристики — динамические поля по выбранной группе (attrs JSONB).
 *   4. Совместимость («метч») — для самоходной выбор прицепных, для прицепной
 *      выбор самоходных. Авиа — секция скрыта.
 *
 * Совместимость сохраняется отдельным PUT-эндпоинтом после save модели (на
 * create — после получения id). EN-перевод отложен (RU-only).
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { describeApiError } from '@/lib/apiError';
import { toast } from '@/lib/toast';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  EditorSection,
  ReferenceCatalogDrawer,
} from '@/components/reference/ReferenceCatalogDrawer';
import { NameTranslationsField } from '@/components/reference/NameTranslationsField';
import { MultiSelectChips } from '@/components/reference/MultiSelectChips';
import { StringTagsField } from '@/components/reference/StringTagsField';
import {
  useMachineryModel,
  useMachineryModels,
  useMachineryTypes,
  useSetMachineryCompatibility,
} from '@/pages/platform-admin/reference/hooks/useMachineryQueries';
import {
  machineryGroupLabel,
  type MachineryFieldDef,
  type MachineryGroup,
  type MachineryModelListItem,
  type MachineryModelPayload,
  type Manufacturer,
  type NameTranslations,
} from '@/pages/platform-admin/reference/machineryCatalogTypes';

interface FormState {
  /** Код марки в справочнике МДМ — по нему матчится книга нормативов. */
  code: string;
  name_translations: NameTranslations;
  /** Другие названия марки. Только выверенные вручную. */
  also_known_as: string[];
  machinery_group: string;
  machinery_type_id: string;
  manufacturer_id: string;
  year_from: string;
  year_to: string;
  /** group-specific attrs, keyed by field def key; raw string/bool values. */
  attrs: Record<string, string | boolean>;
}

const EMPTY_FORM: FormState = {
  code: '',
  name_translations: { ru: '' },
  also_known_as: [],
  machinery_group: '',
  machinery_type_id: '',
  manufacturer_id: '',
  year_from: '',
  year_to: '',
  attrs: {},
};

const numToStr = (n: number | null | undefined): string =>
  n === null || n === undefined ? '' : String(n);

interface FormErrors {
  name?: string;
  group?: string;
  years?: string;
}

function validate(f: FormState): FormErrors {
  const errs: FormErrors = {};
  const hasName = Object.values(f.name_translations).some((v) => v.trim().length > 0);
  if (!hasName) errs.name = 'Требуется название';
  if (!f.machinery_group) errs.group = 'Выберите группу';
  const yf = f.year_from ? Number(f.year_from) : null;
  const yt = f.year_to ? Number(f.year_to) : null;
  if (yf !== null && yt !== null && yf > yt) {
    errs.years = `Год начала (${yf}) не может быть больше года окончания (${yt})`;
  }
  return errs;
}

const numOrNull = (v: string): number | null => (v.trim() ? Number(v) : null);

/** Coerce raw attr inputs into typed JSON per the group's field schema. */
function buildAttrs(
  raw: Record<string, string | boolean>,
  schema: MachineryFieldDef[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const def of schema) {
    const v = raw[def.key];
    if (v === undefined || v === '' || v === null) continue;
    if (def.type === 'number') {
      const n = Number(v);
      if (!Number.isNaN(n)) out[def.key] = n;
    } else if (def.type === 'bool') {
      out[def.key] = Boolean(v);
    } else {
      out[def.key] = v;
    }
  }
  return out;
}

function toPayload(f: FormState, schema: MachineryFieldDef[]): MachineryModelPayload {
  const trimmed: NameTranslations = {};
  for (const [k, v] of Object.entries(f.name_translations)) {
    if (v.trim()) trimmed[k] = v.trim();
  }
  return {
    code: f.code.trim() || null,
    name_translations: trimmed,
    // Дубли схлопываем здесь — сервер отобьёт их 409-м, но доводить до ошибки
    // на очевидном незачем.
    also_known_as: Array.from(new Set(f.also_known_as.map((v) => v.trim()).filter(Boolean))),
    manufacturer_id: f.manufacturer_id || null,
    machinery_group: f.machinery_group,
    machinery_type_id: f.machinery_type_id || null,
    year_from: numOrNull(f.year_from),
    year_to: numOrNull(f.year_to),
    attrs: buildAttrs(f.attrs, schema),
  };
}

export interface MachineryEditorDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  model: MachineryModelListItem | null;
  groups: MachineryGroup[];
  manufacturers: Manufacturer[];
  onSubmit: (payload: MachineryModelPayload) => Promise<void>;
}

export function MachineryEditorDrawer({
  open,
  onOpenChange,
  mode,
  model,
  groups,
  manufacturers,
  onSubmit,
}: MachineryEditorDrawerProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [compatible, setCompatible] = useState<string[]>([]);

  const editId = open && mode === 'edit' && model ? model.id : null;
  const detailQ = useMachineryModel(editId);
  const detailLoading = Boolean(editId) && detailQ.isLoading;

  // Types for the selected group (drives the type select).
  const typesQ = useMachineryTypes(form.machinery_group || undefined);
  const types = typesQ.data?.items ?? [];

  const setCompatMut = useSetMachineryCompatibility();

  // The selected group's polymorph schema.
  const selectedGroup = useMemo(
    () => groups.find((g) => g.code === form.machinery_group),
    [groups, form.machinery_group],
  );
  const schema = selectedGroup?.fields_schema ?? [];

  // Counterpart group for matching (self_propelled <-> implement).
  const counterpartGroup =
    form.machinery_group === 'self_propelled'
      ? 'implement'
      : form.machinery_group === 'implement'
        ? 'self_propelled'
        : null;

  // Candidate models for the matching multi-select (counterpart group, exclude self).
  // Aerial (and any group without a counterpart) has no matching section, so skip
  // the request entirely instead of firing a useless limit:0 query.
  const counterpartModelsQ = useMachineryModels(
    counterpartGroup ? { machinery_group: counterpartGroup, limit: 500 } : { limit: 0 },
    { enabled: Boolean(counterpartGroup) },
  );
  const counterpartOptions = useMemo(
    () =>
      (counterpartModelsQ.data?.items ?? [])
        .filter((m) => m.id !== editId)
        .map((m) => ({ value: m.id, label: m.name_translations.ru ?? m.id })),
    [counterpartModelsQ.data, editId],
  );

  useEffect(() => {
    if (!open) return;
    if (mode === 'create') {
      setForm(EMPTY_FORM);
      setCompatible([]);
      setTouched(false);
      return;
    }
    if (detailQ.data) {
      const d = detailQ.data;
      const rawAttrs: Record<string, string | boolean> = {};
      for (const [k, v] of Object.entries(d.attrs ?? {})) {
        rawAttrs[k] = typeof v === 'boolean' ? v : String(v ?? '');
      }
      setForm({
        code: d.code ?? '',
        name_translations: { ...d.name_translations },
        also_known_as: [...(d.also_known_as ?? [])],
        machinery_group: d.machinery_group,
        machinery_type_id: d.machinery_type_id ?? '',
        manufacturer_id: d.manufacturer_id ?? '',
        year_from: numToStr(d.year_from),
        year_to: numToStr(d.year_to),
        attrs: rawAttrs,
      });
      setCompatible([...(d.compatible_model_ids ?? [])]);
      setTouched(false);
    }
  }, [open, mode, detailQ.data]);

  const errors = validate(form);
  const hasErrors = Object.keys(errors).length > 0;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const setAttr = (key: string, value: string | boolean) =>
    setForm((f) => ({ ...f, attrs: { ...f.attrs, [key]: value } }));

  const handleSave = async () => {
    setTouched(true);
    if (hasErrors) return;
    setSaving(true);
    try {
      const payload = toPayload(form, schema);
      // onSubmit creates/updates the model; for compatibility we need the id.
      // In edit mode we already have it; in create mode we re-set after.
      // onSubmit owns its own success/error feedback (parent toast).
      await onSubmit(payload);
      // Compatibility is only persisted in edit mode here (create needs the new
      // id, which onSubmit does not return to this layer — set it on next open).
      // The model is already saved at this point, so a compat failure must NOT
      // be swallowed: surface a clear toast instead of a mute "saved-but-error"
      // state. We still close the drawer — the model persisted and compatibility
      // can be re-edited on reopen.
      if (editId && counterpartGroup) {
        try {
          await setCompatMut.mutateAsync({
            id: editId,
            payload: { compatible_model_ids: compatible },
          });
        } catch (err) {
          toast.error(
            `Модель сохранена, но совместимость не обновилась: ${
              (err as Error).message
            }. Откройте модель повторно и попробуйте ещё раз.`,
          );
        }
      }
      onOpenChange(false);
    } catch (err) {
      // AK-574 — до этого падение onSubmit (создание/правка модели) уходило
      // в unhandled rejection: drawer оставался открытым молча.
      toast.error(describeApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const show = (err?: string) => (touched && err ? err : undefined);

  return (
    <ReferenceCatalogDrawer
      open={open}
      onOpenChange={onOpenChange}
      mode={mode}
      entityLabel="модели техники"
      headerSlot={
        form.name_translations.ru ? (
          <span className="text-sm font-medium text-foreground">
            {form.name_translations.ru}
            {form.machinery_group && (
              <span className="ml-2 text-xs text-muted-foreground">
                · {machineryGroupLabel(form.machinery_group)}
              </span>
            )}
          </span>
        ) : undefined
      }
      saveDisabled={detailLoading || (touched && hasErrors)}
      saveLoading={saving}
      onSave={handleSave}
    >
      {detailLoading ? (
        <div className="space-y-4 p-1">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <>
          <EditorSection number={1} title="Идентификация">
            <Label className="mb-1 block text-sm">Код МДМ</Label>
            <Input
              value={form.code}
              onChange={(e) => set('code', e.target.value)}
              placeholder="MD-000123"
              className="mb-4 font-mono"
            />
            <Label className="mb-1.5 block text-sm">
              Название (RU) <span className="text-destructive">*</span>
            </Label>
            <NameTranslationsField
              value={form.name_translations}
              onChange={(v) => set('name_translations', v)}
              locales={['ru']}
              hint="EN-перевод отложен. Сейчас ведём только RU."
              hasError={Boolean(show(errors.name))}
            />
            <Label className="mb-1 mt-4 block text-sm">Другие названия</Label>
            <StringTagsField
              values={form.also_known_as}
              onChange={(v) => set('also_known_as', v)}
              placeholder="напр. «К744» (Enter добавит)"
              removeLabel="Убрать название"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Только выверенные названия: по ним марка находится при загрузке нормативов, и
              ошибочное название уведёт норму на чужую технику. Неуверен — оставь пустым,
              такие термины разбираются отдельно.
            </p>
            <Label className="mb-1.5 mt-4 block text-sm">Годы выпуска</Label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  type="number"
                  value={form.year_from}
                  onChange={(e) => set('year_from', e.target.value)}
                  placeholder="с (год)"
                  className={show(errors.years) ? 'border-destructive' : undefined}
                />
                <p className="mt-1 text-xs text-muted-foreground">с года</p>
              </div>
              <div>
                <Input
                  type="number"
                  value={form.year_to}
                  onChange={(e) => set('year_to', e.target.value)}
                  placeholder="до (год)"
                  className={show(errors.years) ? 'border-destructive' : undefined}
                />
                <p className="mt-1 text-xs text-muted-foreground">по год</p>
              </div>
            </div>
            {show(errors.years) && (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-destructive">
                <AlertCircle className="h-3.5 w-3.5" />
                {errors.years}
              </p>
            )}
          </EditorSection>

          <EditorSection number={2} title="Классификация">
            <Label className="mb-1.5 block text-sm">
              Группа <span className="text-destructive">*</span>
            </Label>
            <Select
              value={form.machinery_group || undefined}
              onValueChange={(v) =>
                setForm((f) => ({ ...f, machinery_group: v, machinery_type_id: '', attrs: {} }))
              }
            >
              <SelectTrigger className={show(errors.group) ? 'border-destructive' : undefined}>
                <SelectValue placeholder="Выберите группу…" />
              </SelectTrigger>
              <SelectContent>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.code}>
                    {g.name_translations.ru ?? g.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {show(errors.group) && (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-destructive">
                <AlertCircle className="h-3.5 w-3.5" />
                {errors.group}
              </p>
            )}
            <p className="mt-1.5 text-xs text-muted-foreground">
              Группа определяет набор характеристик ниже.
            </p>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block text-sm">Тип</Label>
                <Select
                  value={form.machinery_type_id || undefined}
                  onValueChange={(v) => set('machinery_type_id', v)}
                  disabled={!form.machinery_group}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Тип…" />
                  </SelectTrigger>
                  <SelectContent>
                    {types.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name_translations.ru ?? t.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-sm">Производитель</Label>
                <Select
                  value={form.manufacturer_id || undefined}
                  onValueChange={(v) => set('manufacturer_id', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Производитель…" />
                  </SelectTrigger>
                  <SelectContent>
                    {manufacturers.map((mf) => (
                      <SelectItem key={mf.id} value={mf.id}>
                        {mf.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </EditorSection>

          {form.machinery_group && schema.length > 0 && (
            <EditorSection
              number={3}
              title="Характеристики"
              hint={machineryGroupLabel(form.machinery_group)}
            >
              <div className="grid grid-cols-2 gap-4">
                {schema.map((def) => (
                  <div key={def.key}>
                    <Label className="mb-1.5 block text-sm">{def.label}</Label>
                    {def.type === 'enum' ? (
                      <Select
                        value={(form.attrs[def.key] as string) || undefined}
                        onValueChange={(v) => setAttr(def.key, v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          {(def.options ?? []).map((opt) => (
                            <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : def.type === 'bool' ? (
                      <div className="flex h-9 items-center">
                        <Switch
                          checked={Boolean(form.attrs[def.key])}
                          onCheckedChange={(v) => setAttr(def.key, v)}
                        />
                      </div>
                    ) : (
                      <Input
                        type={def.type === 'number' ? 'number' : 'text'}
                        value={(form.attrs[def.key] as string) ?? ''}
                        onChange={(e) => setAttr(def.key, e.target.value)}
                        placeholder={def.unit ? def.unit : undefined}
                      />
                    )}
                    {def.help && (
                      <p className="mt-1 text-xs text-muted-foreground">{def.help}</p>
                    )}
                  </div>
                ))}
              </div>
            </EditorSection>
          )}

          {counterpartGroup && (
            <EditorSection
              number={4}
              title="Совместимость (агрегатирование)"
              hint={
                form.machinery_group === 'self_propelled'
                  ? 'какие СХМ можно навесить'
                  : 'каким тягачом агрегатируется'
              }
            >
              {mode === 'create' ? (
                <p className="text-xs text-muted-foreground">
                  Сохраните модель — после этого можно будет задать совместимость
                  при повторном открытии (нужен id новой записи).
                </p>
              ) : (
                <>
                  <MultiSelectChips
                    options={counterpartOptions}
                    selected={compatible}
                    onChange={setCompatible}
                    placeholder={
                      form.machinery_group === 'self_propelled'
                        ? 'Выберите совместимые СХМ…'
                        : 'Выберите совместимые тягачи…'
                    }
                    emptyHint="Нет подходящих моделей в этой группе"
                  />
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Редактируется с обеих сторон. Сохраняется вместе с моделью.
                  </p>
                </>
              )}
            </EditorSection>
          )}
        </>
      )}
    </ReferenceCatalogDrawer>
  );
}

export default MachineryEditorDrawer;
